import { useEffect, useMemo, useRef, useState } from 'react';
import { TaxRateSelect } from '../components/TaxRateSelect.tsx';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { clock, dateTime, day as dayText, money, parseMoney, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Kpi, Table } from './Reports.tsx';

/**
 * Field service: work at the customer's place. The dispatcher sees today's round, plans the week
 * by technician and keeps the calls nobody has yet; the technician sees their own day and does the
 * job from the phone (on the way, start, checklist, parts, report, the customer's signature); the
 * office invoices what is done.
 */

type Tab = 'today' | 'planner' | 'jobs' | 'mine' | 'results' | 'templates' | 'settings';
type JState = 'new' | 'scheduled' | 'on_the_way' | 'working' | 'done' | 'cancelled';
type Kind = 'install' | 'repair' | 'maintenance' | 'inspection' | 'other';
const KINDS: Kind[] = ['repair', 'maintenance', 'install', 'inspection', 'other'];

interface Row {
  id: string;
  number: number;
  title: string;
  kind: Kind;
  priority: 'normal' | 'urgent';
  state: JState;
  customer: string;
  customer_name: string;
  contact: string | null;
  phone: string | null;
  address: string;
  town: string | null;
  technician: string | null;
  technician_name: string | null;
  scheduled_at: string | null;
  duration: number;
  started_at: string | null;
  finished_at: string | null;
  travel_minutes: number;
  work_minutes: number;
  billable: boolean;
  invoice: string | null;
  signed: boolean;
  day?: string;
}
interface Line {
  product: string | null;
  name: string;
  unit: string;
  qty: number;
  unit_price: number;
  tax_rate: number;
}
interface Job extends Omit<Row, 'invoice'> {
  description: string | null;
  location: string | null;
  location_name: string | null;
  template: string | null;
  template_name: string | null;
  checklist: { text: string; done: boolean }[];
  report: string | null;
  signature: string | null;
  signed_by: string | null;
  signed_at: string | null;
  cancel_reason: string | null;
  created_by_name: string | null;
  customer_email: string | null;
  time: { id: string; kind: 'travel' | 'work'; started_at: string; ended_at: string | null; minutes: number; person_name: string | null }[];
  parts: { id: string; product: string | null; name: string; qty: number; unit_price: number; tax_rate: number; from_stock: boolean; by_name: string | null }[];
  events: { at: string; kind: string; text: string | null; by_name: string | null }[];
  bill: { lines: Line[]; billed_minutes: number; net: number; tax: number; total: number };
  invoice: { id: string; name: string | null; state: string; total: number } | null;
  running: { kind: 'travel' | 'work'; started_at: string } | null;
  history: { id: string; number: number; title: string; state: JState; finished_at: string | null; scheduled_at: string | null }[];
}
interface Person {
  id: string;
  name: string;
  technician: boolean;
}
interface Setup {
  people: Person[];
  templates: { id: string; name: string; kind: Kind; title: string | null; duration: number; checklist: string[]; description: string | null }[];
  locations: { id: string; name: string }[];
  products: { id: string; name: string; price: number; tax_rate: number }[];
  customers: { id: string; name: string; address: string | null; town: string | null; phone: string | null }[];
  stock: boolean;
}
interface Overview {
  today: number;
  active: number;
  done_today: number;
  unassigned: number;
  urgent: number;
  late: number;
  to_invoice: number;
  done_week: number;
  work_week: number;
  travel_week: number;
  board: Row[];
  waiting: Row[];
  people: Person[];
  day: string;
  ready: boolean;
}

const STATE: Record<JState, { tone: Tone; icon: IconName }> = {
  new: { tone: 'warn', icon: 'bell' },
  scheduled: { tone: 'info', icon: 'calendar' },
  on_the_way: { tone: 'info', icon: 'truck' },
  working: { tone: 'warn', icon: 'settings' },
  done: { tone: 'ok', icon: 'checkCircle' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const KIND_ICON: Record<Kind, IconName> = { install: 'box', repair: 'settings', maintenance: 'sparkle', inspection: 'eye', other: 'note' };
export const jobName = (n: number) => `J-${n}`;
const hours = (m: number) => (m >= 60 ? t('fs.h_m', { h: Math.floor(m / 60), m: String(m % 60).padStart(2, '0') }) : t('fs.m', { m }));
const mapLink = (r: Pick<Row, 'address' | 'town'>) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([r.address, r.town].filter(Boolean).join(', '))}`;
/** A value for <input type="datetime-local"> in this browser's time. */
const localInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
const addDays = (d: string, n: number) => new Date(Date.parse(`${d}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const shortDay = (d: string) => new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));
const mondayOf = (d: string) => addDays(d, -((new Date(`${d}T12:00:00Z`).getUTCDay() + 6) % 7));

function JobBadge({ j }: { j: { state: JState; invoice: unknown } }) {
  if (j.state === 'done' && j.invoice) return <StatusBadge tone="ok" icon="invoice" label={t('fs.invoiced')} />;
  const s = STATE[j.state];
  return <StatusBadge tone={s.tone} icon={s.icon} label={t(`fs.st_${j.state}` as MessageKey)} pulse={j.state === 'working'} />;
}

export function FieldService() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const dispatch = can('field.dispatch');
  const work = can('field.work');
  const tabs: { key: Tab; icon: IconName; show: boolean }[] = [
    { key: 'today', icon: 'clock', show: true },
    { key: 'mine', icon: 'pin', show: work },
    { key: 'planner', icon: 'calendar', show: true },
    { key: 'jobs', icon: 'list', show: true },
    { key: 'results', icon: 'chart', show: can('field.reports') },
    { key: 'templates', icon: 'layers', show: true },
    { key: 'settings', icon: 'settings', show: can('field.configure') },
  ];
  const visible = tabs.filter((x) => x.show);
  const first: Tab = work && !dispatch ? 'mine' : 'today';
  const tab = (visible.some((x) => x.key === params.get('tab')) ? params.get('tab') : first) as Tab;
  const { data: o, reload } = useAction<Overview>('field.overview');
  const { data: setup, reload: reloadSetup } = useAction<Setup>('field.setup');
  const [version, setVersion] = useState(0);
  const [form, setForm] = useState<{ id?: string; at?: string; technician?: string } | null>(null);
  const open = params.get('j');
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) v === null ? p.delete(k) : p.set(k, v);
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  const openJob = (id: string) => set({ j: id });

  return (
    <Shell>
      <div className="page reports fs-page">
        <div className="page-head">
          <div>
            <h1>{t('fs.title')}</h1>
            <p className="muted">{t('fs.subtitle')}</p>
          </div>
          {dispatch ? (
            <div className="page-actions">
              <Button variant="primary" icon="plus" onClick={() => setForm({})}>
                {t('fs.new')}
              </Button>
            </div>
          ) : null}
        </div>
        {o && !o.ready && can('field.configure') ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>
              {t('fs.no_rate')}{' '}
              <button className="link" onClick={() => set({ tab: 'settings' })}>
                {t('fs.set_rate')}
              </button>
            </span>
          </p>
        ) : null}
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('fs.at_a_glance')}>
            <Kpi label={t('fs.k_today')} value={String(o.today)} hint={o.active ? tn('fs.k_under_way', o.active) : tn('fs.k_done_today', o.done_today)} />
            <Kpi label={t('fs.k_waiting')} value={String(o.unassigned)} hint={o.urgent ? tn('fs.k_urgent', o.urgent) : t('fs.k_none_urgent')} warn={o.urgent > 0} />
            <Kpi label={t('fs.k_late')} value={String(o.late)} hint={t('fs.k_late_hint')} warn={o.late > 0} />
            <Kpi label={t('fs.k_week')} value={String(o.done_week)} hint={t('fs.k_week_hint', { work: hours(o.work_week), travel: hours(o.travel_week) })} />
            {can('field.invoice') ? (
              <Kpi label={t('fs.k_to_invoice')} value={String(o.to_invoice)} hint={o.to_invoice ? t('fs.k_to_invoice_hint') : t('fs.k_all_invoiced')} warn={o.to_invoice > 5} />
            ) : null}
          </section>
        )}
        <div className="segmented tabs fs-tabs" role="tablist" aria-label={t('fs.sections')}>
          {visible.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === first ? null : x.key, f: null })}>
              <Icon name={x.icon} size={16} />
              {t(`fs.tab_${x.key}` as MessageKey)}
            </button>
          ))}
        </div>
        {!o || !setup ? null : tab === 'today' ? (
          <TodayTab o={o} onOpen={openJob} onPlan={dispatch ? (id) => setForm({ id }) : undefined} />
        ) : tab === 'mine' ? (
          <MineTab key={version} onOpen={openJob} />
        ) : tab === 'planner' ? (
          <PlannerTab key={version} onOpen={openJob} onPlan={dispatch ? (at, technician) => setForm({ at, technician }) : undefined} />
        ) : tab === 'jobs' ? (
          <JobsTab key={version} params={params} set={set} onOpen={openJob} people={setup.people} onChange={changed} />
        ) : tab === 'results' ? (
          <ResultsTab />
        ) : tab === 'templates' ? (
          <TemplatesTab
            onChange={async () => {
              await reloadSetup();
            }}
          />
        ) : (
          <SettingsTab onChange={changed} />
        )}
      </div>
      {open && setup ? <JobDialog key={`${open}-${version}`} id={open} setup={setup} onClose={() => set({ j: null })} onChange={changed} onEdit={(id) => setForm({ id })} /> : null}
      {form && setup ? (
        <JobForm
          id={form.id ?? null}
          at={form.at}
          technician={form.technician}
          setup={setup}
          onClose={() => setForm(null)}
          onSaved={async (id) => {
            setForm(null);
            await changed();
            set({ j: id });
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- today --------------------------------------------------------------------------------------------

function JobCard({ j, onOpen, showWho, action: extra }: { j: Row; onOpen: (id: string) => void; showWho?: boolean; action?: React.ReactNode }) {
  return (
    <li className={`fs-card${j.priority === 'urgent' && (j.state === 'new' || j.state === 'scheduled') ? ' urgent' : ''} st-${j.state}`}>
      <button className={`fs-card-main${j.scheduled_at ? '' : ' no-time'}`} onClick={() => onOpen(j.id)}>
        {j.scheduled_at ? <span className="fs-card-time tabular">{clock(j.scheduled_at)}</span> : null}
        <span className="fs-card-body">
          <strong>
            <Icon name={KIND_ICON[j.kind]} size={14} /> {j.title}
          </strong>
          <span className="muted small">{[j.customer_name, j.town, showWho ? j.technician_name : null].filter(Boolean).join(' · ')}</span>
        </span>
        <JobBadge j={j} />
      </button>
      {extra}
    </li>
  );
}

function TodayTab({ o, onOpen, onPlan }: { o: Overview; onOpen: (id: string) => void; onPlan?: (id: string) => void }) {
  const byPerson = useMemo(() => {
    const groups = new Map<string, { name: string; jobs: Row[] }>();
    for (const j of o.board) {
      const key = j.technician ?? '';
      const g = groups.get(key) ?? { name: j.technician_name ?? t('fs.nobody'), jobs: [] };
      g.jobs.push(j);
      groups.set(key, g);
    }
    // Everyone who does field work shows, even with nothing today.
    for (const p of o.people.filter((p) => p.technician)) if (!groups.has(p.id)) groups.set(p.id, { name: p.name, jobs: [] });
    return [...groups.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [o]);
  return (
    <div className="shop-overview">
      <section className="panel">
        <div className="prot-history-head">
          <div>
            <h2 className="rep-sub">{t('fs.today_round', { day: dayText(o.day) })}</h2>
            <p className="muted small">{t('fs.today_hint')}</p>
          </div>
        </div>
        {byPerson.length ? (
          <div className="fs-people">
            {byPerson.map(([id, g]) => {
              const now = g.jobs.find((j) => j.state === 'working' || j.state === 'on_the_way');
              return (
                <section key={id} className="fs-person">
                  <header>
                    <span className="mkt-avatar" aria-hidden="true">
                      {g.name.slice(0, 1)}
                    </span>
                    <span className="vat-party">
                      <strong>{g.name}</strong>
                      <span className="muted small">
                        {now ? t(now.state === 'working' ? 'fs.now_working' : 'fs.now_road', { what: now.customer_name }) : g.jobs.length ? tn('fs.n_jobs', g.jobs.length) : t('fs.free_today')}
                      </span>
                    </span>
                  </header>
                  {g.jobs.length ? (
                    <ul className="fs-cards">
                      {g.jobs.map((j) => (
                        <JobCard key={j.id} j={j} onOpen={onOpen} />
                      ))}
                    </ul>
                  ) : null}
                </section>
              );
            })}
          </div>
        ) : (
          <EmptyState icon="pin" title={t('fs.no_people')} body={t('fs.no_people_hint')} />
        )}
      </section>
      <div className="shop-side">
        <section className="panel">
          <h2 className="rep-sub">{t('fs.waiting')}</h2>
          <p className="muted small">{t('fs.waiting_hint')}</p>
          {o.waiting.length ? (
            <ul className="fs-cards">
              {o.waiting.map((j) => (
                <JobCard
                  key={j.id}
                  j={j}
                  onOpen={onOpen}
                  action={
                    onPlan ? (
                      <Button size="sm" variant="ghost" icon="calendar" onClick={() => onPlan(j.id)}>
                        {t('fs.plan')}
                      </Button>
                    ) : undefined
                  }
                />
              ))}
            </ul>
          ) : (
            <p className="muted">{t('fs.nothing_waiting')}</p>
          )}
        </section>
      </div>
    </div>
  );
}

// ---- my day -------------------------------------------------------------------------------------------

function MineTab({ onOpen }: { onOpen: (id: string) => void }) {
  const { data } = useAction<{ today: string; now: Row[]; today_jobs: Row[]; later: Row[] }>('field.mine');
  if (!data) return <Skeleton h={300} />;
  const group = (title: string, rows: Row[], empty?: string) =>
    rows.length || empty ? (
      <section className="panel">
        <h2 className="rep-sub">{title}</h2>
        {rows.length ? (
          <ul className="fs-cards">
            {rows.map((j) => (
              <li key={j.id} className={`fs-card fs-mine st-${j.state}`}>
                <button className="fs-card-main" onClick={() => onOpen(j.id)}>
                  <span className="fs-card-time tabular">{j.scheduled_at ? (j.day === data.today ? clock(j.scheduled_at) : shortDay(j.day ?? '')) : '—'}</span>
                  <span className="fs-card-body">
                    <strong>{j.title}</strong>
                    <span className="muted small">{j.customer_name}</span>
                    <span className="small">{[j.address, j.town].filter(Boolean).join(', ')}</span>
                  </span>
                  <JobBadge j={j} />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">{empty}</p>
        )}
      </section>
    ) : null;
  return (
    <div className="form-stack">
      {group(t('fs.mine_now'), data.now)}
      {group(t('fs.mine_today'), data.today_jobs, t('fs.mine_free'))}
      {group(t('fs.mine_later'), data.later)}
    </div>
  );
}

// ---- planner ------------------------------------------------------------------------------------------

function PlannerTab({ onOpen, onPlan }: { onOpen: (id: string) => void; onPlan?: (at: string, technician: string) => void }) {
  const [from, setFrom] = useState(() => mondayOf(todayIso()));
  const { data } = useAction<{ days: string[]; people: Person[]; jobs: Row[]; unscheduled: Row[] }>('field.planner', { from, days: 7 });
  const today = todayIso();
  return (
    <section className="panel">
      <div className="toolbar">
        <div className="row-actions">
          <IconButton icon="back" label={t('fs.prev_week')} onClick={() => setFrom(addDays(from, -7))} />
          <Button size="sm" variant="ghost" onClick={() => setFrom(mondayOf(today))}>
            {t('fs.this_week')}
          </Button>
          <IconButton icon="chevronRight" label={t('fs.next_week')} onClick={() => setFrom(addDays(from, 7))} />
          <strong className="fs-week">{t('fs.week_of', { from: dayText(from), to: dayText(addDays(from, 6)) })}</strong>
        </div>
      </div>
      {!data ? (
        <Skeleton h={320} />
      ) : (
        <div className="fs-plan-wrap">
          <div className="fs-plan" style={{ gridTemplateColumns: `minmax(120px, 160px) repeat(${data.days.length}, minmax(120px, 1fr))` }} role="table" aria-label={t('fs.tab_planner')}>
            <div className="fs-plan-corner" role="columnheader" />
            {data.days.map((d) => (
              <div key={d} role="columnheader" className={`fs-plan-day${d === today ? ' today' : ''}`}>
                {shortDay(d)}
              </div>
            ))}
            {data.people.map((p) => (
              <div key={p.id} className="fs-plan-row" role="row">
                <div className="fs-plan-person" role="rowheader">
                  <strong>{p.name}</strong>
                  <span className="muted small">{tn('fs.n_jobs', data.jobs.filter((j) => j.technician === p.id).length)}</span>
                </div>
                {data.days.map((d) => {
                  const cell = data.jobs.filter((j) => j.technician === p.id && j.day === d);
                  const minutes = cell.reduce((s, j) => s + j.duration, 0);
                  return (
                    <div key={d} role="cell" className={`fs-plan-cell${d === today ? ' today' : ''}${minutes > 8 * 60 ? ' full' : ''}`}>
                      {cell.map((j) => (
                        <button
                          key={j.id}
                          className={`fs-chip st-${j.state}${j.priority === 'urgent' ? ' urgent' : ''}`}
                          onClick={() => onOpen(j.id)}
                          title={`${jobName(j.number)} · ${j.title} · ${j.customer_name}`}
                        >
                          <span className="tabular">{j.scheduled_at ? clock(j.scheduled_at) : ''}</span>
                          <span className="fs-chip-text">{j.customer_name}</span>
                        </button>
                      ))}
                      {onPlan && d >= today ? (
                        <button className="fs-add" aria-label={t('fs.plan_for', { name: p.name, day: dayText(d) })} onClick={() => onPlan(`${d}T09:00`, p.id)}>
                          <Icon name="plus" size={14} />
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          {!data.people.length ? <EmptyState icon="users" title={t('fs.no_people')} body={t('fs.no_people_hint')} /> : null}
        </div>
      )}
      {data?.unscheduled.length ? (
        <div className="fs-unplanned">
          <h3 className="rep-sub">{t('fs.waiting')}</h3>
          <ul className="fs-cards">
            {data.unscheduled.map((j) => (
              <JobCard key={j.id} j={j} onOpen={onOpen} />
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

// ---- jobs list ----------------------------------------------------------------------------------------

const FILTERS = ['open', 'new', 'active', 'to_invoice', 'done', 'cancelled', 'all'] as const;

function JobsTab({
  params,
  set,
  onOpen,
  people,
  onChange,
}: {
  params: URLSearchParams;
  set: (n: Record<string, string | null>) => void;
  onOpen: (id: string) => void;
  people: Person[];
  onChange: () => Promise<void>;
}) {
  const { can } = useSession();
  const { busy, run } = useSave();
  const filter = (FILTERS as readonly string[]).includes(params.get('f') ?? '') ? (params.get('f') as (typeof FILTERS)[number]) : 'open';
  const [who, setWho] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  useEffect(() => {
    const id = setTimeout(() => (setQ(search.trim()), setOffset(0)), 300);
    return () => clearTimeout(id);
  }, [search]);
  const { data, reload } = useAction<{ total: number; rows: Row[] }>('field.jobs', { filter, offset, ...(who ? { technician: who } : {}), ...(q ? { search: q } : {}) });
  const invoicing = filter === 'to_invoice' && can('field.invoice');
  const pickedRows = data?.rows.filter((r) => picked.has(r.id)) ?? [];
  const oneCustomer = new Set(pickedRows.map((r) => r.customer)).size === 1;
  const toggle = (r: Row) => {
    const next = new Set(picked);
    if (next.has(r.id)) next.delete(r.id);
    else {
      // One invoice is for one customer: picking another customer starts again.
      for (const p of pickedRows) if (p.customer !== r.customer) next.delete(p.id);
      next.add(r.id);
    }
    setPicked(next);
  };
  const csv = () =>
    data &&
    downloadCsv(
      'field-jobs.csv',
      [t('fs.c_job'), t('fs.c_customer'), t('fs.town'), t('fs.c_who'), t('fs.c_when'), t('fs.c_work'), t('fs.c_road'), t('fs.c_state')],
      data.rows.map((r) => [
        `${jobName(r.number)} ${r.title}`,
        r.customer_name,
        r.town ?? '',
        r.technician_name ?? '',
        r.scheduled_at ?? '',
        r.work_minutes,
        r.travel_minutes,
        t(`fs.st_${r.state}` as MessageKey),
      ]),
    );
  return (
    <section className="panel">
      <div className="toolbar">
        <div className="segmented subtle mkt-filters" role="radiogroup" aria-label={t('fs.show')}>
          {FILTERS.filter((f) => f !== 'to_invoice' || can('field.invoice')).map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => (set({ f: f === 'open' ? null : f }), setOffset(0), setPicked(new Set()))}>
              {t(`fs.f_${f}` as MessageKey)}
            </button>
          ))}
        </div>
        <span className="row-actions fs-tools">
          <select className="input" aria-label={t('fs.c_who')} value={who} onChange={(e) => (setWho(e.target.value), setOffset(0))}>
            <option value="">{t('fs.everyone')}</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <label className="search">
            <Icon name="search" size={16} />
            <input className="input" type="search" value={search} placeholder={t('fs.search')} aria-label={t('fs.search')} onChange={(e) => setSearch(e.target.value)} />
          </label>
          <IconButton icon="download" label={t('fs.csv')} onClick={csv} disabled={!data?.rows.length} />
        </span>
      </div>
      {invoicing && pickedRows.length ? (
        <div className="fs-invoice-bar" role="status">
          <span>{t('fs.picked', { n: pickedRows.length, customer: pickedRows[0]!.customer_name })}</span>
          <Button
            variant="primary"
            icon="invoice"
            busy={busy}
            disabled={!oneCustomer}
            onClick={() =>
              void run(async () => {
                await action('field.invoice', { ids: pickedRows.map((r) => r.id) });
                setPicked(new Set());
                await reload();
                await onChange();
              }, t('fs.invoice_made'))
            }
          >
            {t('fs.make_invoice')}
          </Button>
        </div>
      ) : invoicing ? (
        <p className="muted small">{t('fs.pick_hint')}</p>
      ) : null}
      {!data ? (
        <Skeleton h={240} />
      ) : (
        <>
          <Table
            head={[...(invoicing ? [''] : []), t('fs.c_job'), t('fs.c_who'), t('fs.c_when'), t('fs.c_work'), t('fs.c_state')]}
            phoneHide={invoicing ? [2, 3, 4] : [1, 2, 3]}
            left={invoicing ? [1, 2] : [0, 1]}
            empty={t('fs.none_found')}
            rows={data.rows.map((r) => ({
              key: r.id,
              onClick: () => onOpen(r.id),
              cells: [
                ...(invoicing
                  ? [
                      <input
                        key="pick"
                        type="checkbox"
                        aria-label={t('fs.pick_job', { n: jobName(r.number) })}
                        checked={picked.has(r.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggle(r)}
                      />,
                    ]
                  : []),
                <span className="vat-party" key="j">
                  <span>
                    {r.priority === 'urgent' && r.state !== 'done' && r.state !== 'cancelled' ? <Icon name="alert" size={14} /> : null} {r.title}
                  </span>
                  <span className="muted">
                    {jobName(r.number)} · {r.customer_name}
                    {r.town ? ` · ${r.town}` : ''}
                  </span>
                </span>,
                r.technician_name ?? <span className="muted">—</span>,
                <span key="w" className="tabular">
                  {r.finished_at ? dateTime(r.finished_at) : r.scheduled_at ? dateTime(r.scheduled_at) : <span className="muted">—</span>}
                </span>,
                <span key="m" className="tabular">
                  {r.work_minutes ? hours(r.work_minutes) : '—'}
                </span>,
                <JobBadge key="s" j={r} />,
              ],
            }))}
          />
          <div className="row-actions mkt-pager">
            <span className="muted small">{t('mkt.showing', { from: data.total ? offset + 1 : 0, to: offset + data.rows.length, total: data.total })}</span>
            <IconButton icon="back" label={t('mkt.prev')} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 100))} />
            <IconButton icon="chevronRight" label={t('mkt.next')} disabled={offset + data.rows.length >= data.total} onClick={() => setOffset(offset + 100)} />
          </div>
        </>
      )}
    </section>
  );
}

// ---- one job ------------------------------------------------------------------------------------------

function JobDialog({ id, setup, onClose, onChange, onEdit }: { id: string; setup: Setup; onClose: () => void; onChange: () => Promise<void>; onEdit: (id: string) => void }) {
  const { can, me } = useSession();
  const { busy, run } = useSave();
  const { data: j, reload } = useAction<Job>('field.job', { id });
  const [ask, setAsk] = useState<null | 'finish' | 'pause' | 'sign' | 'cancel' | 'reopen' | 'part' | 'invoice'>(null);
  const [report, setReport] = useState<string | null>(null);
  useEffect(() => {
    if (j && report === null) setReport(j.report ?? '');
  }, [j]);
  // While the job is under way the time on it counts up.
  const [, tickNow] = useState(0);
  useEffect(() => {
    if (!j?.running) return;
    const timer = setInterval(() => tickNow((n) => n + 1), 30_000);
    return () => clearInterval(timer);
  }, [j?.running]);
  const done = async () => {
    setAsk(null);
    await reload();
    await onChange();
  };
  if (!j) {
    return (
      <Dialog title={t('fs.job')} onClose={onClose} size="xl">
        <Skeleton h={360} />
      </Dialog>
    );
  }
  const mine = j.technician === me?.userId;
  const canWork = can('field.work') && (mine || can('field.dispatch'));
  const open = j.state !== 'done' && j.state !== 'cancelled';
  const step = (to: 'on_the_way' | 'working') =>
    void run(
      async () => {
        await action('field.progress', { id: j.id, to });
        await done();
      },
      to === 'on_the_way' ? t('fs.on_the_way_msg') : t('fs.started_msg'),
    );
  const running = j.running ? Math.max(0, Math.round((Date.now() - Date.parse(j.running.started_at)) / 60_000)) : 0;
  const ticked = j.checklist.filter((c) => c.done).length;
  return (
    <>
      <Dialog
        title={`${jobName(j.number)} · ${j.title}`}
        onClose={onClose}
        size="xl"
        footer={
          <>
            {open && can('field.dispatch') ? (
              <>
                <Button variant="ghost" icon="x" onClick={() => setAsk('cancel')}>
                  {t('fs.cancel_job')}
                </Button>
                {j.state === 'new' || j.state === 'scheduled' ? (
                  <Button variant="ghost" icon="edit" onClick={() => onEdit(j.id)}>
                    {t('fs.edit')}
                  </Button>
                ) : null}
              </>
            ) : null}
            {j.state === 'done' && !j.invoice && can('field.dispatch') ? (
              <Button variant="ghost" icon="undo" onClick={() => setAsk('reopen')}>
                {t('fs.reopen')}
              </Button>
            ) : null}
            {j.state === 'done' && j.billable && !j.invoice && can('field.invoice') ? (
              <Button variant="primary" icon="invoice" onClick={() => setAsk('invoice')}>
                {t('fs.make_invoice')}
              </Button>
            ) : null}
            {canWork && (j.state === 'working' || j.state === 'done') && !j.signed_at ? (
              <Button icon="edit" onClick={() => setAsk('sign')}>
                {t('fs.get_signature')}
              </Button>
            ) : null}
            {canWork && ACTIVE.includes(j.state) ? (
              <Button variant="ghost" icon="pause" onClick={() => setAsk('pause')}>
                {t('fs.pause')}
              </Button>
            ) : null}
            {canWork && (j.state === 'scheduled' || (j.state === 'new' && can('field.dispatch') && j.technician)) ? (
              <>
                <Button icon="truck" busy={busy} onClick={() => step('on_the_way')}>
                  {t('fs.on_the_way')}
                </Button>
                <Button variant="primary" icon="settings" busy={busy} onClick={() => step('working')}>
                  {t('fs.start')}
                </Button>
              </>
            ) : null}
            {canWork && j.state === 'on_the_way' ? (
              <Button variant="primary" icon="settings" busy={busy} onClick={() => step('working')}>
                {t('fs.arrived')}
              </Button>
            ) : null}
            {canWork && j.state === 'working' ? (
              <Button variant="primary" icon="checkCircle" onClick={() => setAsk('finish')}>
                {t('fs.finish')}
              </Button>
            ) : null}
          </>
        }
      >
        <div className="form-stack mkt-dlg fs-dlg">
          <div className="item-summary">
            <div>
              <span className="muted">{t('fs.c_customer')}</span>
              <strong>{j.customer_name}</strong>
            </div>
            <div>
              <span className="muted">{t('fs.c_who')}</span>
              <strong>{j.technician_name ?? '—'}</strong>
            </div>
            <div>
              <span className="muted">{t('fs.c_when')}</span>
              <strong>{j.scheduled_at ? `${dateTime(j.scheduled_at)} · ${hours(j.duration)}` : t('fs.not_planned')}</strong>
            </div>
            <JobBadge j={j} />
          </div>
          {j.running ? (
            <p className="callout callout-info">
              <Icon name={j.running.kind === 'travel' ? 'truck' : 'clock'} size={16} />
              <span>{t(j.running.kind === 'travel' ? 'fs.on_road_for' : 'fs.working_for', { time: hours(running) })}</span>
            </p>
          ) : j.state === 'cancelled' ? (
            <p className="callout callout-warn">
              <Icon name="x" size={16} />
              <span>{t('fs.cancelled_because', { reason: j.cancel_reason ?? '' })}</span>
            </p>
          ) : null}
          <div className="shop-detail">
            <section>
              <h3 className="rep-sub">{t('fs.where')}</h3>
              <dl className="sub-moves">
                <div>
                  <dt>{t('fs.address')}</dt>
                  <dd>
                    <a className="link" href={mapLink(j)} target="_blank" rel="noreferrer noopener">
                      {[j.address, j.town].filter(Boolean).join(', ')}
                    </a>
                  </dd>
                </div>
                {j.contact ? (
                  <div>
                    <dt>{t('fs.contact')}</dt>
                    <dd>{j.contact}</dd>
                  </div>
                ) : null}
                {j.phone ? (
                  <div>
                    <dt>{t('fs.phone')}</dt>
                    <dd>
                      <a className="link" href={`tel:${j.phone.replace(/[^\d+]/g, '')}`}>
                        {j.phone}
                      </a>
                    </dd>
                  </div>
                ) : null}
                <div>
                  <dt>{t('fs.kind')}</dt>
                  <dd>
                    {t(`fs.kind_${j.kind}` as MessageKey)}
                    {j.priority === 'urgent' ? ` · ${t('fs.urgent')}` : ''}
                  </dd>
                </div>
              </dl>
              {j.description ? <p className="fs-note">{j.description}</p> : null}
              {j.history.length ? (
                <>
                  <h3 className="rep-sub">{t('fs.before_here')}</h3>
                  <ul className="shop-ways">
                    {j.history.map((h) => (
                      <li key={h.id}>
                        <span className="vat-party">
                          <span>{h.title}</span>
                          <span className="muted small">
                            {jobName(h.number)} · {h.finished_at ? dateTime(h.finished_at) : h.scheduled_at ? dateTime(h.scheduled_at) : '—'}
                          </span>
                        </span>
                        <span className="muted small">{t(`fs.st_${h.state}` as MessageKey)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </section>
            <section>
              <div className="fs-sec-head">
                <h3 className="rep-sub">{t('fs.checklist')}</h3>
                {j.checklist.length ? <span className="muted small">{t('fs.ticked', { n: ticked, total: j.checklist.length })}</span> : null}
              </div>
              {j.checklist.length ? (
                <ul className="fs-checks">
                  {j.checklist.map((c, i) => (
                    <li key={i}>
                      <label className="check-row">
                        <input
                          type="checkbox"
                          checked={c.done}
                          disabled={!canWork || !open || busy}
                          onChange={(e) =>
                            void run(async () => {
                              await action('field.tick', { id: j.id, item: i, done: e.target.checked });
                              await reload();
                            })
                          }
                        />
                        <span>{c.text}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">{t('fs.no_checklist')}</p>
              )}
            </section>
          </div>
          <section>
            <div className="fs-sec-head">
              <h3 className="rep-sub">{t('fs.parts')}</h3>
              {canWork && open ? (
                <Button size="sm" variant="ghost" icon="plus" onClick={() => setAsk('part')}>
                  {t('fs.add_part')}
                </Button>
              ) : null}
            </div>
            {j.parts.length ? (
              <ul className="shop-ways">
                {j.parts.map((p) => (
                  <li key={p.id}>
                    <span className="vat-party">
                      <span>
                        {p.qty} × {p.name}
                      </span>
                      <span className="muted small">{[money(p.unit_price), p.from_stock ? t('fs.from_stock') : null, p.by_name].filter(Boolean).join(' · ')}</span>
                    </span>
                    <strong className="tabular">{money(Math.round(p.qty * p.unit_price))}</strong>
                    {canWork && open ? (
                      <IconButton
                        icon="trash"
                        label={t('fs.remove_part')}
                        onClick={() =>
                          void run(
                            async () => {
                              await action('field.remove_part', { part: p.id });
                              await reload();
                            },
                            p.from_stock ? t('fs.part_back') : undefined,
                          )
                        }
                      />
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">{t('fs.no_parts')}</p>
            )}
          </section>
          <section>
            <h3 className="rep-sub">{t('fs.report')}</h3>
            {canWork && open ? (
              <div className="form-stack">
                <textarea
                  className="input textarea"
                  rows={3}
                  value={report ?? ''}
                  maxLength={4000}
                  placeholder={t('fs.report_hint')}
                  aria-label={t('fs.report')}
                  onChange={(e) => setReport(e.target.value)}
                />
                {report !== (j.report ?? '') ? (
                  <div className="row-actions">
                    <Button
                      size="sm"
                      icon="check"
                      busy={busy}
                      onClick={() =>
                        void run(async () => {
                          await action('field.write_report', { id: j.id, report });
                          await reload();
                        }, t('fs.report_saved'))
                      }
                    >
                      {t('fs.save_report')}
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : j.report ? (
              <p className="fs-note">{j.report}</p>
            ) : (
              <p className="muted">{t('fs.no_report')}</p>
            )}
          </section>
          <div className="shop-detail">
            <section>
              <h3 className="rep-sub">{t('fs.time')}</h3>
              {j.time.length ? (
                <ul className="shop-ways">
                  {j.time.map((x) => (
                    <li key={x.id}>
                      <span className="vat-party">
                        <span>
                          <Icon name={x.kind === 'travel' ? 'truck' : 'clock'} size={14} /> {t(`fs.time_${x.kind}` as MessageKey)}
                        </span>
                        <span className="muted small">
                          {dateTime(x.started_at)}
                          {x.ended_at ? ` – ${clock(x.ended_at)}` : ` · ${t('fs.running')}`} · {x.person_name}
                        </span>
                      </span>
                      <strong className="tabular">{x.ended_at ? hours(x.minutes) : '…'}</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">{t('fs.no_time')}</p>
              )}
              <p className="muted small">{t('fs.time_total', { work: hours(j.work_minutes), travel: hours(j.travel_minutes) })}</p>
            </section>
            <section>
              <h3 className="rep-sub">{j.invoice ? t('fs.invoice_named', { name: j.invoice.name ?? t('fs.draft') }) : j.billable ? t('fs.charge') : t('fs.not_charged')}</h3>
              {j.billable ? (
                <>
                  <ul className="shop-ways">
                    {j.bill.lines.map((l, i) => (
                      <li key={i}>
                        <span className="vat-party">
                          <span>{l.name}</span>
                          <span className="muted small">
                            {l.unit === 'h' ? t('fs.hours_at', { h: l.qty, rate: money(l.unit_price) }) : `${l.qty} × ${money(l.unit_price)}`} · {t('fs.vat', { rate: l.tax_rate })}
                          </span>
                        </span>
                        <strong className="tabular">{money(Math.round(l.qty * l.unit_price))}</strong>
                      </li>
                    ))}
                  </ul>
                  <p className="fs-total">
                    <span className="muted">{t('fs.total_with_vat')}</span>
                    <strong className="tabular">{money(j.bill.total)}</strong>
                  </p>
                  {j.bill.billed_minutes && j.bill.billed_minutes !== j.work_minutes ? (
                    <p className="muted small">{t('fs.billed_note', { billed: hours(j.bill.billed_minutes), worked: hours(j.work_minutes) })}</p>
                  ) : null}
                </>
              ) : (
                <p className="muted">{t('fs.not_charged_hint')}</p>
              )}
            </section>
          </div>
          {j.signature ? (
            <section>
              <h3 className="rep-sub">{t('fs.signature')}</h3>
              <figure className="fs-signature">
                <img src={j.signature} alt={t('fs.signature_of', { name: j.signed_by ?? '' })} />
                <figcaption className="muted small">{t('fs.signed_by', { name: j.signed_by ?? '', at: dateTime(j.signed_at!) })}</figcaption>
              </figure>
            </section>
          ) : null}
          <details className="fs-history">
            <summary>{t('fs.history')}</summary>
            <ul className="shop-ways">
              {j.events.map((e, i) => (
                <li key={i}>
                  <span className="vat-party">
                    <span>{t(`fs.ev_${e.kind}` as MessageKey)}</span>
                    <span className="muted small">{[dateTime(e.at), e.by_name, e.text].filter(Boolean).join(' · ')}</span>
                  </span>
                </li>
              ))}
            </ul>
          </details>
        </div>
      </Dialog>
      {ask === 'finish' || ask === 'pause' ? <FinishDialog j={j} pause={ask === 'pause'} report={report ?? ''} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'sign' ? <SignDialog j={j} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'part' ? <PartDialog j={j} setup={setup} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'cancel' || ask === 'reopen' ? <ReasonDialog j={j} kind={ask} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'invoice' ? (
        <Dialog
          title={t('fs.invoice_title', { n: jobName(j.number) })}
          onClose={() => setAsk(null)}
          size="sm"
          footer={
            <Button
              variant="primary"
              icon="invoice"
              busy={busy}
              onClick={() =>
                void run(async () => {
                  await action('field.invoice', { ids: [j.id] });
                  await done();
                }, t('fs.invoice_made'))
              }
            >
              {t('fs.make_invoice')}
            </Button>
          }
        >
          <p>{t('fs.invoice_note', { total: money(j.bill.total), customer: j.customer_name })}</p>
        </Dialog>
      ) : null}
    </>
  );
}
const ACTIVE: JState[] = ['on_the_way', 'working'];

function FinishDialog({ j, pause, report, onClose, onDone }: { j: Job; pause: boolean; report: string; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [text, setText] = useState(report);
  const open = j.checklist.filter((c) => !c.done);
  return (
    <Dialog
      title={pause ? t('fs.pause_title') : t('fs.finish_title', { n: jobName(j.number) })}
      onClose={onClose}
      size="sm"
      footer={
        <Button
          variant="primary"
          icon={pause ? 'pause' : 'checkCircle'}
          busy={busy}
          disabled={!pause && !text.trim()}
          onClick={() =>
            void run(
              async () => {
                await action('field.progress', { id: j.id, to: pause ? 'paused' : 'done', report: text.trim() || null });
                await onDone();
              },
              pause ? t('fs.paused_msg') : t('fs.done_msg'),
            )
          }
        >
          {pause ? t('fs.pause') : t('fs.finish')}
        </Button>
      }
    >
      <div className="form-stack">
        {!pause && open.length ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>{tn('fs.open_items', open.length)}</span>
          </p>
        ) : null}
        <Field label={pause ? t('fs.pause_why') : t('fs.report')} hint={pause ? t('fs.pause_hint') : t('fs.report_hint')} required={!pause}>
          <textarea className="input textarea" rows={4} value={text} maxLength={4000} onChange={(e) => setText(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function ReasonDialog({ j, kind, onClose, onDone }: { j: Job; kind: 'cancel' | 'reopen'; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [reason, setReason] = useState('');
  return (
    <Dialog
      title={kind === 'cancel' ? t('fs.cancel_title', { n: jobName(j.number) }) : t('fs.reopen_title', { n: jobName(j.number) })}
      onClose={onClose}
      size="sm"
      footer={
        <Button
          variant={kind === 'cancel' ? 'danger' : 'primary'}
          icon={kind === 'cancel' ? 'x' : 'undo'}
          busy={busy}
          disabled={reason.trim().length < 3}
          onClick={() =>
            void run(
              async () => {
                await action(kind === 'cancel' ? 'field.cancel' : 'field.reopen', { id: j.id, reason: reason.trim() });
                await onDone();
              },
              kind === 'cancel' ? t('fs.cancelled_msg') : t('fs.reopened_msg'),
            )
          }
        >
          {kind === 'cancel' ? t('fs.cancel_job') : t('fs.reopen')}
        </Button>
      }
    >
      <div className="form-stack">
        <p>{kind === 'cancel' ? (j.parts.some((p) => p.from_stock) ? t('fs.cancel_note_stock') : t('fs.cancel_note')) : t('fs.reopen_note')}</p>
        <Field label={t('fs.reason')} required>
          <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function PartDialog({ j, setup, onClose, onDone }: { j: Job; setup: Setup; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [product, setProduct] = useState('');
  const [name, setName] = useState('');
  const [qty, setQty] = useState('1');
  const [price, setPrice] = useState('');
  const [search, setSearch] = useState('');
  const p = setup.products.find((x) => x.id === product);
  const found = search.trim().length >= 1 ? setup.products.filter((x) => x.name.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8) : [];
  const amount = Number(qty.replace(',', '.'));
  const typedPrice = price ? parseMoney(price) : null;
  const ok = amount > 0 && (p || (name.trim() && typedPrice !== null));
  return (
    <Dialog
      title={t('fs.add_part')}
      onClose={onClose}
      size="sm"
      footer={
        <Button
          variant="primary"
          icon="plus"
          busy={busy}
          disabled={!ok}
          onClick={() =>
            void run(async () => {
              await action('field.add_part', { id: j.id, product: p?.id ?? null, name: p ? null : name.trim(), qty: amount, unit_price: typedPrice });
              await onDone();
            }, t('fs.part_added'))
          }
        >
          {t('fs.add')}
        </Button>
      }
    >
      <div className="form-stack">
        {p ? (
          <div className="fs-picked">
            <span className="vat-party">
              <strong>{p.name}</strong>
              <span className="muted small">
                {money(p.price)} · {t('fs.vat', { rate: p.tax_rate })}
              </span>
            </span>
            <IconButton icon="x" label={t('fs.change')} onClick={() => setProduct('')} />
          </div>
        ) : (
          <>
            <Field label={t('fs.product')}>
              <label className="search">
                <Icon name="search" size={16} />
                <input className="input" type="search" value={search} placeholder={t('fs.search_products')} onChange={(e) => setSearch(e.target.value)} data-autofocus />
              </label>
            </Field>
            {found.length ? (
              <ul className="shop-ways">
                {found.map((x) => (
                  <li key={x.id}>
                    <span className="vat-party">
                      <span>{x.name}</span>
                      <span className="muted small">{money(x.price)}</span>
                    </span>
                    <Button size="sm" icon="check" onClick={() => (setProduct(x.id), setPrice(''))}>
                      {t('fs.choose')}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
            <Field label={t('fs.or_write')} hint={t('fs.or_write_hint')}>
              <input className="input" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
            </Field>
          </>
        )}
        <div className="form-grid">
          <Field label={t('fs.qty')}>
            <input className="input" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
          </Field>
          <Field label={t('fs.unit_price')} hint={p ? t('fs.price_from_product') : undefined}>
            <input className="input" inputMode="decimal" value={price} placeholder={p ? (p.price / 100).toFixed(2) : ''} onChange={(e) => setPrice(e.target.value)} />
          </Field>
        </div>
        {p && setup.stock ? <p className="muted small">{t('fs.stock_note')}</p> : null}
      </div>
    </Dialog>
  );
}

/** The customer signs with a finger on the phone, or with the mouse. */
function SignDialog({ j, onClose, onDone }: { j: Job; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const canvas = useRef<HTMLCanvasElement>(null);
  const [name, setName] = useState(j.contact ?? '');
  const [drawn, setDrawn] = useState(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    const c = canvas.current!;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.clientWidth * ratio;
    c.height = c.clientHeight * ratio;
    const g = c.getContext('2d')!;
    g.scale(ratio, ratio);
    g.lineWidth = 2.4;
    g.lineCap = 'round';
    g.lineJoin = 'round';
    g.strokeStyle = getComputedStyle(c).getPropertyValue('--paper-ink').trim() || 'black';
  }, []);
  const point = (e: React.PointerEvent) => {
    const r = canvas.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const down = (e: React.PointerEvent) => {
    canvas.current!.setPointerCapture(e.pointerId);
    last.current = point(e);
  };
  const move = (e: React.PointerEvent) => {
    if (!last.current) return;
    const p = point(e);
    const g = canvas.current!.getContext('2d')!;
    g.beginPath();
    g.moveTo(last.current.x, last.current.y);
    g.lineTo(p.x, p.y);
    g.stroke();
    last.current = p;
    setDrawn(true);
  };
  const up = () => {
    last.current = null;
  };
  const clear = () => {
    const c = canvas.current!;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    setDrawn(false);
  };
  return (
    <Dialog
      title={t('fs.sign_title')}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="ghost" icon="undo" onClick={clear} disabled={!drawn}>
            {t('fs.clear')}
          </Button>
          <span className="foot-actions">
            <Button
              variant="primary"
              icon="check"
              busy={busy}
              disabled={!drawn || !name.trim()}
              onClick={() =>
                void run(async () => {
                  await action('field.sign', { id: j.id, signature: canvas.current!.toDataURL('image/png'), signed_by: name.trim() });
                  await onDone();
                }, t('fs.signed_msg'))
              }
            >
              {t('fs.save_signature')}
            </Button>
          </span>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('fs.sign_note', { total: j.billable ? money(j.bill.total) : '—' })}</p>
        <canvas ref={canvas} className="fs-pad" aria-label={t('fs.sign_here')} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
        <Field label={t('fs.signer')} required>
          <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- the job form -------------------------------------------------------------------------------------

function JobForm({ id, at, technician, setup, onClose, onSaved }: { id: string | null; at?: string; technician?: string; setup: Setup; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const [ready, setReady] = useState(!id);
  const [f, setF] = useState({
    title: '',
    kind: 'repair' as Kind,
    priority: 'normal' as 'normal' | 'urgent',
    customer: '',
    contact: '',
    phone: '',
    address: '',
    town: '',
    description: '',
    duration: 60,
    location: '',
    template: '',
    checklist: [] as string[],
    billable: true,
    technician: technician ?? '',
    when: at ?? '',
  });
  const [line, setLine] = useState('');
  const [search, setSearch] = useState('');
  const [tried, setTried] = useState(false);
  const [overlaps, setOverlaps] = useState<{ name: string; title: string; scheduled_at: string }[] | null>(null);
  useEffect(() => {
    if (!id) return;
    void action<Job>('field.job', { id }).then((j) => {
      setF({
        title: j.title,
        kind: j.kind,
        priority: j.priority,
        customer: j.customer,
        contact: j.contact ?? '',
        phone: j.phone ?? '',
        address: j.address,
        town: j.town ?? '',
        description: j.description ?? '',
        duration: j.duration,
        location: j.location ?? '',
        template: j.template ?? '',
        checklist: j.checklist.map((c) => c.text),
        billable: j.billable,
        technician: technician ?? j.technician ?? '',
        when: at ?? (j.scheduled_at ? localInput(new Date(j.scheduled_at)) : ''),
      });
      setReady(true);
    });
  }, [id]);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const cust = setup.customers.find((c) => c.id === f.customer);
  const useTemplate = (tid: string) => {
    const tpl = setup.templates.find((x) => x.id === tid);
    setF((x) => ({
      ...x,
      template: tid,
      ...(tpl ? { kind: tpl.kind, duration: tpl.duration, title: x.title || tpl.title || '', checklist: tpl.checklist, description: x.description || tpl.description || '' } : {}),
    }));
  };
  const found = search.trim().length >= 2 && !cust ? setup.customers.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8) : [];
  const errors = {
    title: !f.title.trim() ? t('fs.e_title') : null,
    customer: !f.customer ? t('fs.e_customer') : null,
    address: !f.address.trim() && !cust?.address ? t('fs.e_address') : null,
    plan: !!f.technician !== !!f.when ? t('fs.e_plan') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      const out = await action<{ job: { id: string }; overlaps: { name: string; title: string; scheduled_at: string }[] }>('field.save', {
        id,
        title: f.title.trim(),
        kind: f.kind,
        priority: f.priority,
        customer: f.customer,
        contact: f.contact.trim() || null,
        phone: f.phone.trim() || null,
        address: f.address.trim() || null,
        town: f.town.trim() || null,
        description: f.description.trim() || null,
        duration: f.duration,
        location: f.location || null,
        template: f.template || null,
        checklist: f.checklist,
        billable: f.billable,
        technician: f.technician || null,
        scheduled_at: f.when ? new Date(f.when).toISOString() : null,
      });
      if (out.overlaps.length && !overlaps) {
        setOverlaps(out.overlaps);
        return;
      }
      await onSaved(out.job.id);
    }, t('fs.saved'));
  };
  const err = (e: string | null) => (tried ? e : null);
  return (
    <Dialog
      title={id ? t('fs.edit_title') : t('fs.new')}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions">
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {f.technician && f.when ? t('fs.save_plan') : t('fs.save')}
          </Button>
        </span>
      }
    >
      {!ready ? (
        <Skeleton h={420} />
      ) : (
        <div className="form-stack sub-form">
          {overlaps ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={16} />
              <span>{t('fs.overlap', { list: overlaps.map((o) => `${o.name} ${clock(o.scheduled_at)}`).join(', ') })}</span>
            </p>
          ) : null}
          <Field label={t('fs.c_customer')} required error={err(errors.customer)} hint={cust ? [cust.address, cust.town, cust.phone].filter(Boolean).join(' · ') : undefined}>
            {cust ? (
              <div className="fs-picked">
                <strong>{cust.name}</strong>
                {!id ? <IconButton icon="x" label={t('fs.change')} onClick={() => (set('customer', ''), setSearch(''))} /> : null}
              </div>
            ) : (
              <label className="search">
                <Icon name="search" size={16} />
                <input className="input" type="search" value={search} placeholder={t('fs.search_customers')} onChange={(e) => setSearch(e.target.value)} data-autofocus />
              </label>
            )}
          </Field>
          {found.length ? (
            <ul className="shop-ways">
              {found.map((c) => (
                <li key={c.id}>
                  <span className="vat-party">
                    <span>{c.name}</span>
                    <span className="muted small">{[c.address, c.town].filter(Boolean).join(', ') || t('fs.no_address')}</span>
                  </span>
                  <Button size="sm" icon="check" onClick={() => set('customer', c.id)}>
                    {t('fs.choose')}
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
          {setup.templates.length ? (
            <Field label={t('fs.template')} hint={t('fs.template_hint')}>
              <select className="input" value={f.template} onChange={(e) => useTemplate(e.target.value)}>
                <option value="">{t('fs.no_template')}</option>
                {setup.templates.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          <Field label={t('fs.what')} required error={err(errors.title)}>
            <input className="input" value={f.title} maxLength={160} placeholder={t('fs.what_example')} onChange={(e) => set('title', e.target.value)} />
          </Field>
          <div className="form-grid">
            <Field label={t('fs.kind')}>
              <select className="input" value={f.kind} onChange={(e) => set('kind', e.target.value as Kind)}>
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`fs.kind_${k}` as MessageKey)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('fs.duration')}>
              <select className="input" value={f.duration} onChange={(e) => set('duration', Number(e.target.value))}>
                {[15, 30, 45, 60, 90, 120, 180, 240, 360, 480].map((m) => (
                  <option key={m} value={m}>
                    {hours(m)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="setting-row">
            <div>
              <strong>{t('fs.urgent')}</strong>
              <p className="muted">{t('fs.urgent_hint')}</p>
            </div>
            <Switch checked={f.priority === 'urgent'} onChange={(v) => set('priority', v ? 'urgent' : 'normal')} label={t('fs.urgent')} />
          </div>
          <div className="form-grid">
            <Field label={t('fs.address')} error={err(errors.address)} hint={cust?.address ? t('fs.address_hint') : undefined}>
              <input className="input" value={f.address} maxLength={200} placeholder={cust?.address ?? ''} onChange={(e) => set('address', e.target.value)} />
            </Field>
            <Field label={t('fs.town')}>
              <input className="input" value={f.town} maxLength={80} placeholder={!f.address ? (cust?.town ?? '') : ''} onChange={(e) => set('town', e.target.value)} />
            </Field>
          </div>
          <div className="form-grid">
            <Field label={t('fs.contact')} hint={t('fs.optional')}>
              <input className="input" value={f.contact} maxLength={80} onChange={(e) => set('contact', e.target.value)} />
            </Field>
            <Field label={t('fs.phone')} hint={t('fs.optional')}>
              <input className="input" type="tel" value={f.phone} maxLength={40} placeholder={cust?.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
            </Field>
          </div>
          <Field label={t('fs.description')} counter={`${f.description.length}/4000`}>
            <textarea className="input textarea" rows={3} value={f.description} maxLength={4000} placeholder={t('fs.description_hint')} onChange={(e) => set('description', e.target.value)} />
          </Field>
          <fieldset className="option-group">
            <legend>{t('fs.checklist')}</legend>
            {f.checklist.length ? (
              <ol className="fs-lines">
                {f.checklist.map((c, i) => (
                  <li key={`${c}-${i}`}>
                    <span>{c}</span>
                    <IconButton
                      icon="x"
                      label={t('fs.remove')}
                      onClick={() =>
                        set(
                          'checklist',
                          f.checklist.filter((_, k) => k !== i),
                        )
                      }
                    />
                  </li>
                ))}
              </ol>
            ) : (
              <p className="muted small">{t('fs.no_checklist')}</p>
            )}
            <div className="mkt-check">
              <input
                className="input"
                value={line}
                maxLength={160}
                placeholder={t('fs.add_line')}
                aria-label={t('fs.add_line')}
                onChange={(e) => setLine(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && line.trim()) {
                    e.preventDefault();
                    set('checklist', [...f.checklist, line.trim()]);
                    setLine('');
                  }
                }}
              />
              <Button icon="plus" disabled={!line.trim() || f.checklist.length >= 40} onClick={() => (set('checklist', [...f.checklist, line.trim()]), setLine(''))}>
                {t('fs.add')}
              </Button>
            </div>
          </fieldset>
          <fieldset className="option-group">
            <legend>{t('fs.plan_section')}</legend>
            <p className="muted small">{t('fs.plan_hint')}</p>
            <div className="form-grid">
              <Field label={t('fs.c_who')}>
                <select className="input" value={f.technician} onChange={(e) => set('technician', e.target.value)}>
                  <option value="">{t('fs.later')}</option>
                  {setup.people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('fs.c_when')} error={err(errors.plan)}>
                <input className="input" type="datetime-local" value={f.when} onChange={(e) => set('when', e.target.value)} />
              </Field>
            </div>
          </fieldset>
          <div className="form-grid">
            {setup.locations.length > 1 ? (
              <Field label={t('fs.parts_from')}>
                <select className="input" value={f.location} onChange={(e) => set('location', e.target.value)}>
                  <option value="">{t('fs.main_place')}</option>
                  {setup.locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
          </div>
          <div className="setting-row">
            <div>
              <strong>{t('fs.billable')}</strong>
              <p className="muted">{t('fs.billable_hint')}</p>
            </div>
            <Switch checked={f.billable} onChange={(v) => set('billable', v)} label={t('fs.billable')} />
          </div>
        </div>
      )}
    </Dialog>
  );
}

// ---- results ------------------------------------------------------------------------------------------

function ResultsTab() {
  const [from, setFrom] = useState(() => addDays(todayIso(), -29));
  const [to, setTo] = useState(todayIso);
  const { data } = useAction<{
    by_person: { id: string; name: string; jobs: number; work: number; travel: number; signed: number; late_avg: number | null }[];
    by_kind: { kind: Kind; jobs: number; avg_work: number }[];
    invoiced: number;
    invoices: number;
    repairs: number;
    repeat: number;
  }>('field.report', { from, to });
  const jobs = data?.by_person.reduce((s, p) => s + p.jobs, 0) ?? 0;
  const work = data?.by_person.reduce((s, p) => s + p.work, 0) ?? 0;
  const travel = data?.by_person.reduce((s, p) => s + p.travel, 0) ?? 0;
  return (
    <div className="form-stack">
      <section className="panel">
        <div className="toolbar">
          <div className="row-actions">
            <Field label={t('fs.from')}>
              <input className="input" type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} />
            </Field>
            <Field label={t('fs.to')}>
              <input className="input" type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} />
            </Field>
          </div>
        </div>
        {!data ? (
          <Skeleton h={200} />
        ) : (
          <section className="rep-kpis">
            <Kpi label={t('fs.r_jobs')} value={String(jobs)} hint={t('fs.k_week_hint', { work: hours(work), travel: hours(travel) })} />
            <Kpi label={t('fs.r_road_share')} value={work + travel ? `${Math.round((travel / (work + travel)) * 100)}%` : '—'} hint={t('fs.r_road_hint')} />
            <Kpi
              label={t('fs.r_first_time')}
              value={data.repairs ? `${Math.round(((data.repairs - data.repeat) / data.repairs) * 100)}%` : '—'}
              hint={t('fs.r_first_time_hint', { n: data.repeat })}
              warn={data.repairs > 0 && data.repeat / data.repairs > 0.15}
            />
            <Kpi label={t('fs.r_invoiced')} value={money(data.invoiced)} hint={tn('fs.n_invoices', data.invoices)} />
          </section>
        )}
      </section>
      {data ? (
        <div className="shop-detail">
          <section className="panel">
            <h2 className="rep-sub">{t('fs.r_by_person')}</h2>
            <Table
              head={[t('fs.c_who'), t('fs.r_jobs'), t('fs.c_work'), t('fs.c_road'), t('fs.r_signed')]}
              phoneHide={[3, 4]}
              left={[0]}
              empty={t('fs.r_nothing')}
              rows={data.by_person.map((p) => ({ key: p.id, cells: [p.name, String(p.jobs), hours(p.work), hours(p.travel), `${Math.round((p.signed / Math.max(1, p.jobs)) * 100)}%`] }))}
            />
          </section>
          <section className="panel">
            <h2 className="rep-sub">{t('fs.r_by_kind')}</h2>
            <Table
              head={[t('fs.kind'), t('fs.r_jobs'), t('fs.r_avg')]}
              left={[0]}
              empty={t('fs.r_nothing')}
              rows={data.by_kind.map((k) => ({ key: k.kind, cells: [t(`fs.kind_${k.kind}` as MessageKey), String(k.jobs), hours(k.avg_work)] }))}
            />
          </section>
        </div>
      ) : null}
    </div>
  );
}

// ---- templates and settings ---------------------------------------------------------------------------

interface TemplateRow {
  id: string;
  name: string;
  kind: Kind;
  title: string | null;
  duration: number;
  checklist: string[];
  description: string | null;
  active: boolean;
  jobs: number;
}

function TemplatesTab({ onChange }: { onChange: () => Promise<void> }) {
  const { can } = useSession();
  const { data, reload } = useAction<TemplateRow[]>('field.templates');
  const [edit, setEdit] = useState<TemplateRow | 'new' | null>(null);
  return (
    <section className="panel">
      <div className="prot-history-head">
        <div>
          <h2 className="rep-sub">{t('fs.templates')}</h2>
          <p className="muted small">{t('fs.templates_hint')}</p>
        </div>
        {can('field.configure') ? (
          <Button size="sm" icon="plus" onClick={() => setEdit('new')}>
            {t('fs.new_template')}
          </Button>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={200} />
      ) : data.length ? (
        <ul className="sub-plans">
          {data.map((x) => (
            <li key={x.id} className={x.active ? '' : 'inactive'}>
              <div className="sub-plan-head">
                <strong>{x.name}</strong>
                <StatusBadge tone="neutral" icon={KIND_ICON[x.kind]} label={t(`fs.kind_${x.kind}` as MessageKey)} />
              </div>
              <span className="muted small">
                {hours(x.duration)} · {tn('fs.n_lines', x.checklist.length)} · {tn('fs.n_jobs', x.jobs)}
              </span>
              <span className="small">
                {x.checklist.slice(0, 3).join(' · ')}
                {x.checklist.length > 3 ? ' …' : ''}
              </span>
              {can('field.configure') ? (
                <div className="row-actions">
                  <Button size="sm" variant="ghost" icon="edit" onClick={() => setEdit(x)}>
                    {t('fs.edit')}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="layers" title={t('fs.no_templates')} body={t('fs.no_templates_hint')} />
      )}
      {edit ? (
        <TemplateDialog
          x={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={async () => {
            setEdit(null);
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </section>
  );
}

function TemplateDialog({ x, onClose, onSaved }: { x: TemplateRow | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [name, setName] = useState(x?.name ?? '');
  const [kind, setKind] = useState<Kind>(x?.kind ?? 'maintenance');
  const [title, setTitle] = useState(x?.title ?? '');
  const [duration, setDuration] = useState(x?.duration ?? 60);
  const [lines, setLines] = useState((x?.checklist ?? []).join('\n'));
  const [description, setDescription] = useState(x?.description ?? '');
  const [active, setActive] = useState(x?.active ?? true);
  const [tried, setTried] = useState(false);
  const list = lines
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  return (
    <Dialog
      title={x ? x.name : t('fs.new_template')}
      onClose={onClose}
      size="md"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (!name.trim() || list.length > 40) return;
              void run(async () => {
                await action('field.save_template', {
                  id: x?.id ?? null,
                  name: name.trim(),
                  kind,
                  title: title.trim() || null,
                  duration,
                  checklist: list,
                  description: description.trim() || null,
                  active,
                });
                await onSaved();
              }, t('fs.saved'));
            }}
          >
            {t('fs.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack sub-form">
        <Field label={t('fs.name')} required error={tried && !name.trim() ? t('fs.e_name') : null}>
          <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="form-grid">
          <Field label={t('fs.kind')}>
            <select className="input" value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`fs.kind_${k}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('fs.duration')}>
            <select className="input" value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
              {[15, 30, 45, 60, 90, 120, 180, 240, 360, 480].map((m) => (
                <option key={m} value={m}>
                  {hours(m)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label={t('fs.default_title')} hint={t('fs.optional')}>
          <input className="input" value={title} maxLength={160} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label={t('fs.checklist')} hint={t('fs.lines_hint')} error={tried && list.length > 40 ? t('fs.e_lines') : null}>
          <textarea className="input textarea" rows={6} value={lines} onChange={(e) => setLines(e.target.value)} />
        </Field>
        <Field label={t('fs.description')} hint={t('fs.optional')}>
          <textarea className="input textarea" rows={2} value={description} maxLength={4000} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="setting-row">
          <div>
            <strong>{t('fs.in_use')}</strong>
            <p className="muted">{t('fs.in_use_hint')}</p>
          </div>
          <Switch checked={active} onChange={setActive} label={t('fs.in_use')} />
        </div>
      </div>
    </Dialog>
  );
}

interface Settings {
  hourly_rate: number;
  round_minutes: number;
  min_minutes: number;
  travel_fee: number;
  labour_tax_rate: number;
  labour_name: string;
  travel_name: string;
}

function SettingsTab({ onChange }: { onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { data, reload } = useAction<Settings>('field.settings');
  const [f, setF] = useState<Settings & { rate: string; fee: string }>();
  useEffect(() => {
    if (data && !f) setF({ ...data, rate: data.hourly_rate ? (data.hourly_rate / 100).toFixed(2) : '', fee: data.travel_fee ? (data.travel_fee / 100).toFixed(2) : '' });
  }, [data]);
  if (!f) return <Skeleton h={300} />;
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF({ ...f, [k]: v });
  const rate = f.rate ? parseMoney(f.rate) : 0;
  const fee = f.fee ? parseMoney(f.fee) : 0;
  const bad = rate === null || fee === null || !f.labour_name.trim() || !f.travel_name.trim();
  const sample = rate ? Math.round((Math.ceil(Math.max(70, f.min_minutes) / f.round_minutes) * f.round_minutes * rate) / 60) : 0;
  return (
    <div className="form-stack">
      <section className="panel form-stack sub-form">
        <div>
          <h2 className="rep-sub">{t('fs.rates')}</h2>
          <p className="muted small">{t('fs.rates_hint')}</p>
        </div>
        <div className="form-grid">
          <Field label={t('fs.hourly_rate')} hint={t('fs.before_vat')} error={rate === null ? t('fs.e_amount') : null}>
            <input className="input" inputMode="decimal" value={f.rate} onChange={(e) => set('rate', e.target.value)} />
          </Field>
          <Field label={t('fs.travel_fee')} hint={t('fs.travel_fee_hint')} error={fee === null ? t('fs.e_amount') : null}>
            <input className="input" inputMode="decimal" value={f.fee} onChange={(e) => set('fee', e.target.value)} />
          </Field>
          <Field label={t('fs.vat_rate')}>
            <TaxRateSelect value={f.labour_tax_rate} onChange={(r) => set('labour_tax_rate', r)} />
          </Field>
        </div>
        <div className="form-grid">
          <Field label={t('fs.round_to')}>
            <select className="input" value={f.round_minutes} onChange={(e) => set('round_minutes', Number(e.target.value))}>
              {[1, 5, 10, 15, 30, 60].map((m) => (
                <option key={m} value={m}>
                  {m === 1 ? t('fs.exact') : hours(m)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('fs.min_charge')}>
            <select className="input" value={f.min_minutes} onChange={(e) => set('min_minutes', Number(e.target.value))}>
              {[0, 15, 30, 45, 60, 90, 120].map((m) => (
                <option key={m} value={m}>
                  {m ? hours(m) : t('fs.none')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="form-grid">
          <Field label={t('fs.labour_name')} hint={t('fs.on_invoice')}>
            <input className="input" value={f.labour_name} maxLength={80} onChange={(e) => set('labour_name', e.target.value)} />
          </Field>
          <Field label={t('fs.travel_name')} hint={t('fs.on_invoice')}>
            <input className="input" value={f.travel_name} maxLength={80} onChange={(e) => set('travel_name', e.target.value)} />
          </Field>
        </div>
        {rate ? <p className="muted small">{t('fs.example', { billed: hours(Math.ceil(Math.max(70, f.min_minutes) / f.round_minutes) * f.round_minutes), amount: money(sample) })}</p> : null}
        <div className="row-actions mkt-settings-foot">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={bad}
            onClick={() =>
              void run(async () => {
                await action('field.save_settings', {
                  hourly_rate: rate,
                  round_minutes: f.round_minutes,
                  min_minutes: f.min_minutes,
                  travel_fee: fee,
                  labour_tax_rate: f.labour_tax_rate,
                  labour_name: f.labour_name.trim(),
                  travel_name: f.travel_name.trim(),
                });
                await reload();
                await onChange();
              }, t('fs.settings_saved'))
            }
          >
            {t('fs.save')}
          </Button>
        </div>
      </section>
    </div>
  );
}
